import * as React from "react";
import { cn } from "@/lib/utils";
import { MyBillingPlans } from "./BillingPlans";
import { DowngradeModal } from "./DowngradeModal";
import { Button } from "@/components/ui/button";

export enum PlanType {
  FREE = "FREE",
  STARTER = "STARTER",
  PRO = "PRO",
  ULTRA = "ULTRA",
}

// Add a ComingSoon tag component
function ComingSoonTag() {
  return (
    <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded-lg text-xs bg-purple-100 text-purple-800">
      Soon
    </span>
  );
}



// Define features in a structured way for the table
const features = [
  {
    name: "Monthly Pin Limit 📌",
    free: "50 pins",
    starter: "200 pins",
    pro: "500 pins",
    ultra: "Unlimited pins",
    tooltip: "Maximum number of pins you can create and schedule each month",
  },
  {
    name: "Pinterest Accounts 🏢",
    free: "1 account",
    starter: "1 account",
    pro: "3 accounts",
    ultra: "Unlimited accounts",
    tooltip: "Number of Pinterest accounts you can manage simultaneously",
  },
  {
    name: "AI Title & Description Generation 🤖",
    free: "Basic",
    starter: "Basic",
    pro: "Premium",
    ultra: "Premium",
    tooltip:
      "AI-powered title and description generation with SEO optimization",
  },
  {
    name: "Auto-Sync Catalog ⚡",
    free: true,
    starter: true,
    pro: true,
    ultra: true,
    tooltip: "Automatically sync new content/product, title, description, image changes to the pinning schedule without you having to do anything",
  },
  {
    name: "Product Collection Pins 🛍️",
    free: true,
    starter: true,
    pro: true,
    ultra: true,
    tooltip:
      "Pin products from your collections with varying levels of optimization",
  },
  {
    name: "Blog Article Pins 📝",
    free: "Not available",
    starter: "Not available",
    pro: true,
    ultra: true,
    tooltip: "Automatically create pins from your blog articles",
  },
  {
    name: "Tag-Based Article Pinning 🏷️",
    free: "Not available",
    starter: "Not available",
    pro: true,
    ultra: true,
    tooltip: "Pin articles based on tags for better organization",
    comingSoon: true,
  },
  {
    name: "Tag-Based Product Pinning 🎯",
    free: "Not available",
    starter: "Not available",
    pro: true,
    ultra: true,
    tooltip: "Pin products based on tags for better organization",
    comingSoon: true,
  },
  {
    name: "Analytics Dashboard 📊",
    free: true,
    starter: true,
    pro: true,
    ultra: true,
    tooltip:
      "Track and analyze your Pinterest performance with detailed insights",
  },
 
  {
    name: "Intelligent Scheduling 🎯",
    free: false,
    starter: false,
    pro: true,
    ultra: true,
    tooltip: "Schedule pins at optimal times for maximum engagement",
  },
];

// Add PlanButton component
function PlanButton({
  planType,
  currentPlan,
  onClick,
  className,
  duration
}: {
  planType: PlanType;
  currentPlan: PlanType | null;
  onClick: (plan: PlanType, duration: 'monthly' | 'yearly') => void;
  className?: string;
  duration: 'monthly' | 'yearly';
}) {
  if (currentPlan === planType) {
    return (
      <span className="text-sm text-blue-600 font-medium">
        {planType === PlanType.ULTRA ? "You're on the best plan! 🎉" : "Current Plan"}
      </span>
    );
  }

  // Special case for Free plan
  if (planType === PlanType.FREE && currentPlan) {
    return (
      <Button
        className={cn(
          'text-gray-500 px-4 py-2 text-sm font-medium bg-white border border-gray-300 hover:bg-gray-50',
          className
        )}
        disabled={currentPlan !== null}
      >
        {currentPlan ? 'Free Plan' : 'Start with Free'}
      </Button>
    );
  }

  // Don't show upgrade buttons if already on Ultra
  if (currentPlan === PlanType.ULTRA) {
    return (
      <div className="text-center text-sm text-gray-600">
        Already on Ultra
      </div>
    );
  }

  const buttonStyles = {
    [PlanType.FREE]: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
    [PlanType.STARTER]: 'bg-red-600 scale-105 text-white hover:bg-red-700',
    [PlanType.PRO]: 'bg-red-600 scale-105 text-white hover:bg-red-700',
    [PlanType.ULTRA]: 'bg-gradient-to-r scale-105 from-red-600 to-rose-700 text-white hover:from-red-700 hover:to-rose-800'
  };
  
  return (
    <Button
      onClick={() => onClick?.(planType, duration)}
      className={cn(
        'text-sm font-medium transition-all duration-300 px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md',
        buttonStyles[planType],
        className
      )}
    >
      {currentPlan ? `Upgrade to ${planType}` : `Start with ${planType}`}
    </Button>
  );
}

export default function Pricingv2Component({
  currentPlan,
  handleClick,
}: {
  currentPlan: PlanType | null;
  handleClick: (plan: PlanType, duration: 'monthly' | 'yearly') => void;
}) {
  const [isYearly, setIsYearly] = React.useState(false);
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = React.useState(false);

  // Get all plans that are lower than current plan
  const getDowngradePlans = () => {
    if (!currentPlan) return [];
    const planOrder = [PlanType.FREE, PlanType.STARTER, PlanType.PRO, PlanType.ULTRA];
    const currentIndex = planOrder.indexOf(currentPlan);
    return planOrder.slice(0, currentIndex + 1);
  };

  const getMonthlyFromYearly = (yearlyPrice: number) =>
    (yearlyPrice / 12).toFixed(2);

  return (
    <div id='pricing' className="">
      <div className="text-center mb-8 sm:mb-12">
        {/* <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Compare Plans
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8">
          
        </p> */}

        <div className="text-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Simple, transparent pricing ✨
            </h2>
            <p className="mt-4 text-lg text-gray-600">
            Choose the perfect plan for your Pinterest automation needs.
            </p>
          </div>

        {/* Show downgrade button if on a paid plan */}
        {currentPlan && currentPlan !== PlanType.FREE && (
          <div className="mb-6">
            <button
              onClick={() => setIsDowngradeModalOpen(true)}
              className="text-gray-600 hover:text-gray-800 text-sm underline"
            >
              Not feeling good about the subscription?
            </button>
          </div>
        )}

        {/* Downgrade Modal */}
        <DowngradeModal
          currentPlan={currentPlan}
          isOpen={isDowngradeModalOpen}
          onClose={() => setIsDowngradeModalOpen(false)}
          onDowngrade={handleClick}
          plans={getDowngradePlans()}
        />

        {/* Billing Toggle */}
        <div className="inline-flex p-1 bg-white rounded-full shadow-md">
          <button
            onClick={() => setIsYearly(false)}
            className={`px-4 sm:px-4 py-2 rounded-full text-sm font-medium transition-all ${
              !isYearly ? "bg-gray-900 text-white" : "text-gray-600"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setIsYearly(true)}
            className={`px-4 sm:px-4 py-2 rounded-full text-sm font-medium transition-all ${
              isYearly ? "bg-gray-900 text-white" : "text-gray-600"
            }`}
          >
            Annual (Save 16%)
          </button>
        </div>
      </div>

      {/* Desktop View (Hidden on Mobile) */}
      <div className="hidden md:block">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden border">
          <div className="relative">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 z-20 bg-white py-4 px-4 text-left text-gray-600 font-medium border-r">
                    FEATURE
                  </th>
                  <th className="py-4 px-4 text-center text-gray-600 font-medium">
                    FREE
                  </th>
                  <th
                    className={`py-4 px-4 text-center ${currentPlan === PlanType.STARTER ? "bg-blue-50" : ""}`}
                  >
                    <div className="font-medium">STARTER</div>
                    <div className="mt-1">
                      <div className="text-gray-400 line-through text-sm">
                        $
                        {isYearly
                          ? `${MyBillingPlans.regular.STARTER * 12}/year`
                          : `${MyBillingPlans.regular.STARTER}/month`}
                      </div>
                      <div className="text-gray-900 font-medium">
                        {isYearly ? (
                          <>
                            <div>
                              ${getMonthlyFromYearly(MyBillingPlans.yearly.STARTER)}
                              /month
                            </div>
                            <div className="text-xs text-gray-500">
                              ${MyBillingPlans.yearly.STARTER} billed annually
                            </div>
                          </>
                        ) : (
                          <div>${MyBillingPlans.monthly.STARTER}/month</div>
                        )}
                      </div>
                      <div className="text-xs text-green-600 font-medium mt-1">
                        Launch Offer 🚀
                      </div>
                    </div>
                  </th>
                  <th
                    className={`py-4 px-4 text-center ${currentPlan === PlanType.PRO ? "bg-blue-50" : ""}`}
                  >
                    <div className="font-medium">PRO</div>
                    <div className="mt-1">
                      <div className="text-gray-400 line-through text-sm">
                        $
                        {isYearly
                          ? `${MyBillingPlans.regular.PRO * 12}/year`
                          : `${MyBillingPlans.regular.PRO}/month`}
                      </div>
                      <div className="text-gray-900 font-medium">
                        {isYearly ? (
                          <>
                            <div>
                              ${getMonthlyFromYearly(MyBillingPlans.yearly.PRO)}/month
                            </div>
                            <div className="text-xs text-gray-500">
                              ${MyBillingPlans.yearly.PRO} billed annually
                            </div>
                          </>
                        ) : (
                          <div>${MyBillingPlans.monthly.PRO}/month</div>
                        )}
                      </div>
                      <div className="text-xs text-green-600 font-medium mt-1">
                        Launch Offer 🚀
                      </div>
                    </div>
                  </th>
                  <th
                    className={`py-10 px-4 text-center ${currentPlan === PlanType.ULTRA ? "bg-blue-50" : ""}`}
                  >
                    <div className="relative">
                      <div style={{zIndex: '500'}} className="absolute w-full -top-8 -rotate-[10deg] left-1/2 transform -translate-x-1/2">
                        <span className="inline-block bg-gradient-to-r from-red-600 to-rose-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                          Best Value ✨
                        </span>
                      </div>
                      <div className="font-medium">ULTRA</div>
                      <div className="mt-1">
                        <div className="text-gray-400 line-through text-sm">
                          $
                          {isYearly
                            ? `${MyBillingPlans.regular.ULTRA * 12}/year`
                            : `${MyBillingPlans.regular.ULTRA}/month`}
                        </div>
                        <div className="text-gray-900 font-medium">
                          {isYearly ? (
                            <>
                              <div>
                                ${getMonthlyFromYearly(MyBillingPlans.yearly.ULTRA)}/month
                              </div>
                              <div className="text-xs text-gray-500">
                                ${MyBillingPlans.yearly.ULTRA} billed annually
                              </div>
                            </>
                          ) : (
                            <div>${MyBillingPlans.monthly.ULTRA}/month</div>
                          )}
                        </div>
                        <div className="text-xs text-green-600 font-medium mt-1">
                          Launch Offer 🚀
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr
                    key={feature.name}
                    className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="left-0 z-10 py-4 px-4 text-sm text-gray-900 relative group border-r bg-inherit">
                      <div className="flex items-center">
                        <span>{feature.name}</span>
                        {feature.tooltip && (
                          <span className="ml-1 text-gray-400 cursor-help">
                            ⓘ
                            <div className="hidden group-hover:block absolute left-0 -top-2 transform -translate-y-full z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg">
                              {feature.tooltip}
                              <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                          </span>
                        )}
                      {feature.comingSoon && <ComingSoonTag />}

                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof feature.free === "boolean" ? (
                        feature.free ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span
                          className={`text-sm ${
                            feature.free === "Not available"
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          {feature.free}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof feature.starter === "boolean" ? (
                        feature.starter ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span
                          className={`text-sm ${
                            feature.starter === "Not available"
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          {feature.starter}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof feature.pro === "boolean" ? (
                        feature.pro ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span
                          className={`text-sm ${
                            feature.pro === "Not available"
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          {feature.pro}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof feature.ultra === "boolean" ? (
                        feature.ultra ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span
                          className={`text-sm ${
                            feature.ultra === "Not available"
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          {feature.ultra}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t">
                <tr>
                  <td className="py-4 px-4"></td>
                  <td className="py-4 px-4 text-center">
                    <PlanButton planType={PlanType.FREE} currentPlan={currentPlan} onClick={handleClick} duration={isYearly ? 'yearly' : 'monthly'} />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <PlanButton 
                      planType={PlanType.STARTER} 
                      currentPlan={currentPlan} 
                      onClick={handleClick} 
                      duration={isYearly ? 'yearly' : 'monthly'}
                    />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <PlanButton 
                      planType={PlanType.PRO} 
                      currentPlan={currentPlan} 
                      onClick={handleClick} 
                      duration={isYearly ? 'yearly' : 'monthly'}
                    />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <PlanButton 
                      planType={PlanType.ULTRA} 
                      currentPlan={currentPlan} 
                      onClick={handleClick} 
                      duration={isYearly ? 'yearly' : 'monthly'}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile View (Hidden on Desktop) */}
      <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[PlanType.FREE, PlanType.STARTER, PlanType.PRO, PlanType.ULTRA].map(
          (planType) => (
            <div
              key={planType}
              className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                currentPlan === planType
                  ? "border-2 border-blue-500"
                  : "border border-gray-200"
              }`}
            >
              {/* Plan Header */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {planType}
                    </h3>
                    {currentPlan === planType && (
                      <span className="text-sm text-blue-600">
                        Current Plan
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 line-through text-sm">
                      $
                      {isYearly
                        ? `${MyBillingPlans.regular[planType] * 12}/year`
                        : `${MyBillingPlans.regular[planType]}/month`}
                    </div>
                    <div className="text-gray-900 font-medium">
                      {isYearly ? (
                        <>
                          <div>
                            ${getMonthlyFromYearly(MyBillingPlans.yearly[planType])}/month
                          </div>
                          <div className="text-xs text-gray-500">
                            ${MyBillingPlans.yearly[planType]} billed annually
                          </div>
                        </>
                      ) : (
                        <div>${MyBillingPlans.monthly[planType]}/month</div>
                      )}
                    </div>
                    {planType !== PlanType.FREE && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        Launch Offer 🚀
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div className="p-4 space-y-4">
                {features.map((feature) => {
                  const value =
                    feature[planType.toLowerCase() as keyof typeof feature];
                  if (!value) return null;

                  return (
                    <div key={feature.name} className="relative group">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <CheckIcon
                            available={value !== "Not available"}
                            basic={
                              typeof value === "string" &&
                              value.toLowerCase().includes("basic")
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {feature.name}
                            </span>
                            {feature.comingSoon && <ComingSoonTag />}
                            {feature.tooltip && (
                              <button
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() =>
                                  setActiveTooltip(
                                    activeTooltip === feature.name
                                      ? null
                                      : feature.name,
                                  )
                                }
                              >
                                ⓘ
                              </button>
                            )}
                          </div>
                          {typeof value === "string" && (
                            <div
                              className={`text-sm mt-0.5 ${
                                value === "Not available"
                                  ? "text-red-400"
                                  : value.toLowerCase().includes("basic")
                                    ? "text-orange-400"
                                    : "text-gray-600"
                              }`}
                            >
                              {value}
                            </div>
                          )}
                          {/* Mobile Tooltip */}
                          {activeTooltip === feature.name &&
                            feature.tooltip && (
                              <div className="mt-2 p-2 bg-gray-900 text-white text-xs rounded-lg">
                                {feature.tooltip}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Button */}
              <div className="p-4 border-t bg-gray-50">
                <PlanButton 
                  planType={planType} 
                  currentPlan={currentPlan} 
                  onClick={handleClick}
                  duration={isYearly ? 'yearly' : 'monthly'}
                  className={planType !== PlanType.FREE ? "w-full" : ""} 
                />
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

function CheckIcon({
  className,
  available = true,
  basic = false,
}: {
  className?: string;
  available?: boolean;
  basic?: boolean;
}) {
  // If it's a basic feature, show orange bullet
  if (basic) {
    return (
      <svg
        className="w-5 h-5 mx-auto text-orange-400"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <circle cx="10" cy="10" r="8" />
      </svg>
    );
  }

  // If not available, show red cross
  if (!available) {
    return (
      <svg
        className="w-5 h-5 mx-auto text-red-400"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  // Otherwise show green checkmark
  return (
    <svg
      className="w-5 h-5 mx-auto text-green-500"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  );
}
